/**
 * NotificationBell Component
 *
 * Bell icon with unread count badge and dropdown for notifications.
 * Polls for new notifications every 30 seconds.
 * Shakes when new notifications arrive.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import {
  useDismissAllNotifications,
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/hooks/use-notifications';
import { cn } from '@/lib/utils';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/hooks/use-notifications';

export function NotificationBell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const previousCountRef = useRef<number | null>(null);

  // Fetch unread count with polling
  const { data: countData } = useUnreadNotificationCount();
  const unreadCount = countData?.count ?? 0;

  // Fetch notifications when dropdown is opened
  const { data: notificationsData, isLoading } = useNotifications(isOpen);
  const notifications = notificationsData ?? [];

  // Mutations
  const markAsReadMutation = useMarkNotificationRead();
  const markAllAsReadMutation = useMarkAllNotificationsRead();
  const dismissMutation = useDismissNotification();
  const dismissAllMutation = useDismissAllNotifications();

  // Detect new notifications and trigger shake
  useEffect(() => {
    if (previousCountRef.current !== null && unreadCount > previousCountRef.current) {
      // Defer state update to avoid synchronous setState in effect
      const shakeTimer = setTimeout(() => setShouldShake(true), 0);
      const resetTimer = setTimeout(() => setShouldShake(false), 1000);
      return () => {
        clearTimeout(shakeTimer);
        clearTimeout(resetTimer);
      };
    }
    previousCountRef.current = unreadCount;
  }, [unreadCount]);

  // Handle notification click - navigate to issue
  function handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.issueId) {
      setIsOpen(false);
      navigate(`/issues/${notification.issueId}`);
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'relative h-9 w-9 text-inherit hover:text-inherit/80 transition-colors',
            shouldShake && 'bell-shake',
          )}
        >
          <Icon name="notifications" className="h-5 w-5" filled={unreadCount > 0} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[28rem] max-h-[560px] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b dark:border-[hsl(233,40%,25%)]">
          <h3 className="font-semibold text-neutral-900 dark:text-white">
            {t('notifications.title')}
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-neutral-600 dark:text-[hsl(215,20.2%,65.1%)] hover:text-neutral-900 dark:hover:text-white transition-colors"
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isPending}
              >
                {t('notifications.markAllRead')}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-neutral-600 dark:text-[hsl(215,20.2%,65.1%)] hover:text-neutral-900 dark:hover:text-white transition-colors"
                onClick={() => dismissAllMutation.mutate()}
                disabled={dismissAllMutation.isPending}
              >
                {t('notifications.clearAll')}
              </Button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon
                name="progress_activity"
                className="h-6 w-6 animate-spin text-muted-foreground"
              />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-neutral-500 dark:text-[hsl(215,20.2%,65.1%)]">
              <Icon
                name="notifications_off"
                className="h-10 w-10 mb-2 text-neutral-300 dark:text-neutral-600"
              />
              <p className="text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  onDismiss={() => dismissMutation.mutate(notification.id)}
                />
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
